import moment from "moment-timezone";
import { NaiveDateTime, NaiveRange, NaiveDate } from "./datetime/index.js";
/*
  api::bizHours
  [
    {open: 600, close: 1200}, // Monday : iso week day 1
    {open: 600, close: 1200}, // 600 (minutes) => "10:00", 1200 => "20:00"
    {open: 1320, close: 360}, // open time is a referance date. It means store opens at 22:00 Wendsday and closes at 06:00 Thursday.
    {open: 600, close: 1200},
    {open: 600, close: 1200},
    {open: 0, close: 0},      // "00:00" ~ "00:00" means store is closed on the day of week.
    {open: 720, close: 1440}  // Sunday
  ],

  store::businessHours
  {
    active: true,
    dayOfWeek: [
      {open: "09:00",close: "20:00"},
      {open: "09:00",close: "20:00"},
      {open: "09:00",close: "20:00"},
      {open: "09:00",close: "20:00"},
      {open: "09:00",close: "20:00"},
      {open: "09:00",close: "20:00"},
      {open: "09:00",close: "20:00"}
    ]
  }
*/
export class BizSchedule {
    timezone;
    bizHours;
    holidays;
    workdays;
    constructor(schedule, timezone = "UTC") {
        this.timezone = timezone;
        if (Array.isArray(schedule)) {
            this.bizHours = schedule;
            this.holidays = [];
            this.workdays = [];
        }
        else if ("bizHours" in schedule) {
            this.bizHours = schedule.bizHours;
            this.holidays = schedule.holidays || [];
            this.workdays = schedule.workdays || [];
        }
        else {
            this.bizHours = convertStoreBH2ApiBH(schedule.businessHours);
            this.holidays = [];
            this.workdays = [];
        }
    }
    static fromMultipleSchedules(schedules, timezone = "UTC") {
        const bizHours = BizSchedule.getLongestBhRangeCombination(schedules);
        return new BizSchedule(bizHours, timezone);
    }
    _toMoment(at) {
        if (moment.isMoment(at))
            return at.clone();
        // NOTE: DB Time을 사용하는 경우, BizSchedule의 timezone은 UTC를 사용하여야 함
        if (NaiveDateTime.isNaiveDateTime(at))
            return at.toMoment(this.timezone);
        if (NaiveDate.isNaiveDate(at))
            return at.toMoment(this.timezone);
        throw new Error("Unsupported date type");
    }
    isOpen(at, isRoundTime = false) {
        const m = this._toMoment(at);
        return this.isBizHours(m, isRoundTime) && !this.isHoliday(m);
    }
    isBizHours(at, isRoundTime = false) {
        const m = this._toMoment(at);
        const bh = this.getBizHoursOfDay(m);
        const yesterdayBh = this.getBizHoursOfDay(m.clone().subtract(1, "day"));
        let { open, close } = bh;
        if (open === close)
            return false;
        if (open > close || bh.overnight)
            close += 1440;
        let { open: yOpen, close: yClose } = yesterdayBh;
        if (isRoundTime) {
            const roundedTime = getRoundTime({ ...bh, open, close });
            open = roundedTime.open;
            close = roundedTime.close;
            const yRoundedTime = getRoundTime(yesterdayBh);
            yOpen = yRoundedTime.open;
            yClose = yRoundedTime.close;
        }
        const mdx = m.hours() * 60 + m.minutes();
        if (yOpen > yClose && mdx < yClose)
            return true;
        return mdx >= open && mdx < close;
    }
    isHoliday(at) {
        const m = this._toMoment(at);
        const bh = this.getBizHoursOfDay(m);
        const date = m.format("YYYY-MM-DD");
        const isWorkDate = this.workdays.indexOf(date) >= 0;
        if (isWorkDate)
            return false;
        if (bh.open == bh.close)
            return true;
        return this.holidays.indexOf(date) >= 0;
    }
    getBizHoursOfDay(at) {
        const m = this._toMoment(at);
        const isowd = m.isoWeekday() - 1; // isoweekday 1 => Monday
        return this.bizHours[isowd];
    }
    getRangeOfDay(day) {
        const at = day.toMoment(this.timezone);
        const bizHour = this.getBizHoursOfDay(at);
        if (!bizHour.active) {
            return new NaiveRange(NaiveDateTime.fromMoment(at).startOf("day"), NaiveDateTime.fromMoment(at).startOf("day").plus({ day: 1 }), "[]");
        }
        const from = NaiveDateTime.fromMoment(at).set({
            hour: Math.floor(bizHour.open / 60),
            minute: Math.floor(bizHour.open % 60),
            second: 0,
            millisecond: 0,
        });
        let to = NaiveDateTime.fromMoment(at).set({
            hour: Math.floor(bizHour.close / 60),
            minute: Math.floor(bizHour.open % 60),
            second: 0,
            millisecond: 0,
        });
        if (bizHour.overnight) {
            to = to.plus({ day: 1 });
        }
        return new NaiveRange(from, to, "[]");
    }
    setICSDates(holidays, workdays) {
        this.holidays = holidays;
        this.workdays = workdays;
    }
    /**
     * Get the longest business hour range combination from an array of business hours.
     * @param businessHoursArray - An array of business hours.
     * @returns An array representing the longest business hour range for each day of the week.
     */
    static getLongestBhRangeCombination(businessHoursArray) {
        const bizHoursArray = businessHoursArray.map(bh => convertStoreBH2ApiBH(bh));
        const ranges = bizHoursArray
            .reduce((longest, current) => {
            for (let i = 0; i < 7; i++) {
                const currentOpen = current[i].open;
                let currentClose = current[i].close;
                if (currentOpen === currentClose)
                    continue;
                if (currentOpen > currentClose) {
                    currentClose += 1440;
                }
                longest[i][0] = Math.min(longest[i][0], currentOpen);
                longest[i][1] = Math.max(longest[i][1], currentClose);
            }
            return longest;
        }, new Array(7).fill(null).map(_ => [1440, 0]))
            .map(v => (v[0] === 1440 && v[1] === 0 ? [0, 1440] : v)); // If none of the stores are open on a day, set it to 0-1440 (24 hours)
        return ranges.map(([open, close]) => ({
            active: open !== close,
            overnight: close > 1440,
            open,
            close: close > 1440 ? close - 1440 : close,
        }));
    }
}
const convertStrTimeToMinutes = (bh) => {
    const open = moment.duration(bh.open).asMinutes();
    const close = moment.duration(bh.close).asMinutes();
    return {
        active: bh.open != bh.close,
        overnight: open > close,
        open,
        close,
    };
};
const convertStoreBH2ApiBH = (sbh) => {
    if (!sbh.active || sbh.dayOfWeek.length === 0) {
        return Array(7).fill({ overnight: false, open: 0, close: 1440 });
    }
    else {
        if (sbh.dayOfWeek.length === 1) {
            return Array(7).fill(convertStrTimeToMinutes(sbh.dayOfWeek[0]));
        }
        else if (sbh.dayOfWeek.length === 2) {
            const wd = Array(5).fill(convertStrTimeToMinutes(sbh.dayOfWeek[0]));
            const we = Array(2).fill(convertStrTimeToMinutes(sbh.dayOfWeek[1]));
            return wd.concat(we);
        }
        else if (sbh.dayOfWeek.length === 7) {
            return sbh.dayOfWeek.map(convertStrTimeToMinutes);
        }
        else {
            throw new Error("day of week number is incorrect");
        }
    }
};
// open: 10:30 => 10:00
// close: 20:30 => 21:00
const getRoundTime = (bh) => {
    const open = bh.open - (bh.open % 60);
    let close = bh.close;
    if (close % 60 > 0) {
        close += 60 - (close % 60);
    }
    return { open, close };
};
export default BizSchedule;
